import { useState } from 'react'
import { AlertTriangle, Tv, ChevronDown, ChevronUp } from 'lucide-react'
import { useServerMetrics } from '../../hooks/useServerMetrics'
import { RefreshRing } from '../../components/monitoring/RefreshRing'
import { TimeRangeSelector } from '../../components/monitoring/TimeRangeSelector'
import { KPICards } from '../../components/monitoring/KPICards'
import { CpuPanel, CpuCorePanel } from '../../components/monitoring/CpuPanel'
import { MemoryPanel } from '../../components/monitoring/MemoryPanel'
import { DiskPanel } from '../../components/monitoring/DiskPanel'
import { NetworkPanel } from '../../components/monitoring/NetworkPanel'
import { ProcessTables } from '../../components/monitoring/ProcessTables'
import { ConnectionsPanel } from '../../components/monitoring/ConnectionsPanel'
import { SystemInfoPanel } from '../../components/monitoring/SystemInfoPanel'

/**
 * Renders the full Server Monitoring experience for one server.
 * `serverId` defaults to "default" — once a second server is
 * registered (see MonitoredServer / register_monitored_server),
 * pass its server_id here from a server picker in ExecutiveDashboard.
 */
export default function ServerMonitoringTab({ serverId = 'remote01' }: { serverId?: string }) {
  const { data, loading, error, range, lastRefresh, countdown, autoRefreshSeconds, refresh, changeRange } =
    useServerMetrics(serverId, '1h')

  const showingSkeleton = loading && !data
  const [showDetails, setShowDetails] = useState(false)

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-xs text-neutral-600 font-medium">
          {lastRefresh
            ? <>Last updated {lastRefresh.toLocaleTimeString()} · refreshes every {autoRefreshSeconds}s</>
            : 'Loading latest server metrics…'}
        </p>
        <div className="flex items-center gap-3">
          <TimeRangeSelector value={range} onChange={changeRange} disabled={loading} />
          <button
            onClick={refresh}
            disabled={loading}
            title="Refresh now"
            className="flex items-center gap-2 bg-secondary/5 hover:bg-secondary/10 text-secondary px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 text-sm"
          >
            <RefreshRing progress={countdown / autoRefreshSeconds} spinning={loading} dark />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <KPICards cards={data?.cards} loading={showingSkeleton} />

      <div className="space-y-6">
        <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
          <CpuPanel latest={data?.cpu.latest} series={data?.cpu.series || []} loading={showingSkeleton} />
        </div>
        <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
          <MemoryPanel latest={data?.memory.latest} series={data?.memory.series || []} loading={showingSkeleton} />
        </div>
        <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
          <CpuCorePanel latest={data?.cpu.latest} loading={showingSkeleton} />
        </div>
        <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
          <DiskPanel filesystems={data?.disk.filesystems || []} ioSeries={data?.disk.io_series || []} loading={showingSkeleton} />
        </div>
        <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
          <NetworkPanel interfaces={data?.network.interfaces || []} throughputSeries={data?.network.throughput_series || []} loading={showingSkeleton} />
        </div>

        {/* Additional detail panels (Processes, Connections, System Info) are
            intentionally NOT marked data-kiosk-section — they stay collapsed
            by default and are excluded from auto-scroll/kiosk cycling. */}
        <div className="border border-neutral-200 rounded-xl bg-white">
          <button
            onClick={() => setShowDetails((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-secondary hover:bg-neutral-50 transition-colors rounded-xl"
          >
            <span>View Details</span>
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showDetails && (
            <div className="px-4 pb-4 space-y-6 border-t border-neutral-100 pt-4">
              <ProcessTables topCpu={data?.processes.top_cpu || []} topMemory={data?.processes.top_memory || []} loading={showingSkeleton} />
              <ConnectionsPanel latest={data?.connections.latest} series={data?.connections.series || []} loading={showingSkeleton} />
              <SystemInfoPanel system={data?.system} loading={showingSkeleton} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


// import { AlertTriangle } from 'lucide-react'
// import { useServerMetrics } from '../../hooks/useServerMetrics'
// import { RefreshRing } from '../../components/monitoring/RefreshRing'
// import { TimeRangeSelector } from '../../components/monitoring/TimeRangeSelector'
// import { KPICards } from '../../components/monitoring/KPICards'
// import { CpuPanel, CpuCorePanel } from '../../components/monitoring/CpuPanel'
// import { MemoryPanel } from '../../components/monitoring/MemoryPanel'
// import { DiskPanel } from '../../components/monitoring/DiskPanel'
// import { NetworkPanel } from '../../components/monitoring/NetworkPanel'
// import { ProcessTables } from '../../components/monitoring/ProcessTables'
// import { ConnectionsPanel } from '../../components/monitoring/ConnectionsPanel'
// import { SystemInfoPanel } from '../../components/monitoring/SystemInfoPanel'

// /**
//  * Renders the full Server Monitoring experience for one server.
//  * `serverId` defaults to "default" — once a second server is
//  * registered (see MonitoredServer / register_monitored_server),
//  * pass its server_id here from a server picker in ExecutiveDashboard.
//  */
// export default function ServerMonitoringTab({ serverId = 'default' }: { serverId?: string }) {
//   const { data, loading, error, range, lastRefresh, countdown, autoRefreshSeconds, refresh, changeRange } =
//     useServerMetrics(serverId, '1h')

//   const showingSkeleton = loading && !data

//   return (
//     <div>
//       <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
//         <p className="text-xs text-neutral-600 font-medium">
//           {lastRefresh
//             ? <>Last updated {lastRefresh.toLocaleTimeString()} · refreshes every {autoRefreshSeconds}s</>
//             : 'Loading latest server metrics…'}
//         </p>
//         <div className="flex items-center gap-3">
//           <TimeRangeSelector value={range} onChange={changeRange} disabled={loading} />
//           <button
//             onClick={refresh}
//             disabled={loading}
//             title="Refresh now"
//             className="flex items-center gap-2 bg-secondary/5 hover:bg-secondary/10 text-secondary px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 text-sm"
//           >
//             <RefreshRing progress={countdown / autoRefreshSeconds} spinning={loading} dark />
//             <span className="hidden sm:inline">Refresh</span>
//           </button>
//         </div>
//       </div>

//       {error && (
//         <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
//           <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
//           <span className="text-sm">{error}</span>
//         </div>
//       )}

//       <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
//         <KPICards cards={data?.cards} loading={showingSkeleton} />
//       </div>

//       <div className="space-y-6">
//         <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
//           <CpuPanel latest={data?.cpu.latest} series={data?.cpu.series || []} loading={showingSkeleton} />
//         </div>
//         <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
//           <MemoryPanel latest={data?.memory.latest} series={data?.memory.series || []} loading={showingSkeleton} />
//         </div>
//         <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
//           <CpuCorePanel latest={data?.cpu.latest} loading={showingSkeleton} />
//         </div>
//         <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
//           <DiskPanel filesystems={data?.disk.filesystems || []} ioSeries={data?.disk.io_series || []} loading={showingSkeleton} />
//         </div>
//         <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
//           <NetworkPanel interfaces={data?.network.interfaces || []} throughputSeries={data?.network.throughput_series || []} loading={showingSkeleton} />
//         </div>
//         <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
//           <ProcessTables topCpu={data?.processes.top_cpu || []} topMemory={data?.processes.top_memory || []} loading={showingSkeleton} />
//         </div>
//         <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
//           <ConnectionsPanel latest={data?.connections.latest} series={data?.connections.series || []} loading={showingSkeleton} />
//         </div>
//         <div data-kiosk-section style={{ overflowAnchor: 'none' }}>
//           <SystemInfoPanel system={data?.system} loading={showingSkeleton} />
//         </div>
//       </div>
//     </div>
//   )
// }
